import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';
import { useTheme } from '../../context/ThemeContext';
import { supabase } from '../../lib/supabase';
import { sendClaimSubmittedEmail } from '../../lib/emails';

const AGREEMENT_VERSION = '1.0';

const ShieldIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const CheckIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ClaimLocationModal = ({ isOpen, onClose, locationId, locationData, onSuccess }) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Step 1 form
  const [form, setForm] = useState({
    fullName: '',
    role: '',
    contactEmail: '',
    contactPhone: '',
    notes: '',
  });

  // Step 2 agreement
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [signatureName, setSignatureName] = useState('');

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setForm({ fullName: '', role: '', contactEmail: '', contactPhone: '', notes: '' });
      setAgreedToTerms(false);
      setSignatureName('');
      setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const canProceedStep1 = form.fullName.trim() && form.role && form.contactEmail.trim();
  const canSubmit = agreedToTerms && signatureName.trim();

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);

    try {
      // Insert claim
      const { data: claim, error: claimError } = await supabase
        .from('location_claims')
        .insert({
          location_id: locationId,
          claimant_id: activeProfile.id,
          claimant_auth_id: user.id,
          full_name: form.fullName.trim(),
          role_at_business: form.role,
          contact_email: form.contactEmail.trim(),
          contact_phone: form.contactPhone.trim() || null,
          additional_notes: form.notes.trim() || null,
        })
        .select('id')
        .single();

      if (claimError) throw claimError;

      // Insert agreement
      const { error: agreementError } = await supabase
        .from('location_agreements')
        .insert({
          user_id: activeProfile.id,
          claim_id: claim.id,
          agreement_version: AGREEMENT_VERSION,
          signee_name: signatureName.trim(),
        });

      if (agreementError) throw agreementError;

      // Update location submitted_at
      await supabase
        .from('locations')
        .update({ submitted_at: new Date().toISOString() })
        .eq('id', locationId);

      // Send confirmation email (non-blocking)
      sendClaimSubmittedEmail(activeProfile.id, locationId).catch(err =>
        console.error('[Email] Claim confirmation failed:', err)
      );

      setStep(3);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Error submitting claim:', err);
      alert('Failed to submit claim. Please try again.');
      setSubmitting(false);
    }
  };

  const styles = {
    backdrop: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 999,
    },
    sheet: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      maxHeight: '90vh',
      backgroundColor: theme.bgSheet,
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      animation: 'slideUp 0.3s ease-out',
    },
    handle: {
      width: 36,
      height: 4,
      borderRadius: 2,
      backgroundColor: theme.border,
      margin: '12px auto 0',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 16px 8px',
    },
    headerTitle: {
      fontSize: 17,
      fontWeight: 600,
      color: theme.text,
      margin: 0,
    },
    closeBtn: {
      background: 'none',
      border: 'none',
      fontSize: 15,
      color: theme.textMuted,
      cursor: 'pointer',
      padding: 8,
    },
    stepIndicator: {
      display: 'flex',
      gap: 8,
      padding: '0 16px 12px',
    },
    stepDot: {
      flex: 1,
      height: 3,
      borderRadius: 2,
      backgroundColor: theme.border,
    },
    stepDotActive: {
      backgroundColor: theme.accent,
    },
    body: {
      flex: 1,
      overflowY: 'auto',
      padding: '0 16px 16px',
    },
    locationInfo: {
      padding: '12px',
      backgroundColor: theme.bgCard,
      borderRadius: 10,
      border: `1px solid ${theme.border}`,
      marginBottom: 20,
    },
    locationName: {
      fontSize: 16,
      fontWeight: 600,
      color: theme.text,
      margin: 0,
    },
    locationAddress: {
      fontSize: 13,
      color: theme.textSecondary,
      marginTop: 4,
    },
    label: {
      display: 'block',
      fontSize: 13,
      fontWeight: 600,
      color: theme.text,
      marginBottom: 6,
    },
    required: {
      color: '#ef4444',
    },
    input: {
      width: '100%',
      padding: 12,
      borderRadius: 10,
      border: `1px solid ${theme.border}`,
      backgroundColor: theme.bgInput,
      color: theme.text,
      fontSize: 15,
      boxSizing: 'border-box',
      outline: 'none',
    },
    select: {
      width: '100%',
      padding: 12,
      borderRadius: 10,
      border: `1px solid ${theme.border}`,
      backgroundColor: theme.bgInput,
      color: theme.text,
      fontSize: 15,
      boxSizing: 'border-box',
      outline: 'none',
      appearance: 'none',
    },
    textarea: {
      width: '100%',
      padding: 12,
      borderRadius: 10,
      border: `1px solid ${theme.border}`,
      backgroundColor: theme.bgInput,
      color: theme.text,
      fontSize: 15,
      boxSizing: 'border-box',
      outline: 'none',
      minHeight: 80,
      resize: 'vertical',
      fontFamily: 'inherit',
    },
    fieldGroup: {
      marginBottom: 16,
    },
    button: {
      width: '100%',
      padding: 14,
      borderRadius: 12,
      border: 'none',
      backgroundColor: theme.accent,
      color: '#fff',
      fontSize: 16,
      fontWeight: 600,
      cursor: 'pointer',
      marginTop: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
    },
    buttonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    agreementBox: {
      maxHeight: 300,
      overflowY: 'auto',
      padding: 16,
      backgroundColor: theme.bgCard,
      borderRadius: 10,
      border: `1px solid ${theme.border}`,
      marginBottom: 16,
      fontSize: 13,
      color: theme.textSecondary,
      lineHeight: 1.6,
    },
    checkboxRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      marginBottom: 16,
      cursor: 'pointer',
    },
    checkbox: {
      width: 22,
      height: 22,
      borderRadius: 6,
      border: `2px solid ${theme.border}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      marginTop: 1,
    },
    checkboxChecked: {
      backgroundColor: theme.accent,
      border: `2px solid ${theme.accent}`,
    },
    checkboxLabel: {
      fontSize: 14,
      color: theme.text,
      lineHeight: 1.4,
    },
    viewFullLink: {
      color: theme.accent,
      textDecoration: 'underline',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      fontSize: 13,
      padding: 0,
      marginBottom: 16,
      display: 'inline-block',
    },
    successContainer: {
      padding: '40px 16px',
      textAlign: 'center',
    },
    successCircle: {
      width: 64,
      height: 64,
      borderRadius: '50%',
      backgroundColor: 'rgba(34, 197, 94, 0.15)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 16px',
      color: '#22c55e',
    },
    successTitle: {
      fontSize: 20,
      fontWeight: 600,
      color: theme.text,
      margin: '0 0 12px',
    },
    successText: {
      fontSize: 14,
      color: theme.textSecondary,
      lineHeight: 1.5,
      margin: '0 0 8px',
    },
  };

  return (
    <>
      <div style={styles.backdrop} onClick={step === 3 ? onClose : undefined} />
      <div style={styles.sheet}>
        <div style={styles.handle} />

        {/* Header */}
        <div style={styles.header}>
          <h3 style={styles.headerTitle}>
            {step === 1 && 'Claim This Location'}
            {step === 2 && 'Partner Agreement'}
            {step === 3 && 'Claim Submitted'}
          </h3>
          <button style={styles.closeBtn} onClick={onClose}>
            {step === 3 ? 'Done' : 'Cancel'}
          </button>
        </div>

        {/* Step Indicator */}
        {step < 3 && (
          <div style={styles.stepIndicator}>
            <div style={{ ...styles.stepDot, ...styles.stepDotActive }} />
            <div style={{ ...styles.stepDot, ...(step >= 2 ? styles.stepDotActive : {}) }} />
          </div>
        )}

        {/* Body */}
        <div style={styles.body}>
          {/* STEP 1: Business Info */}
          {step === 1 && (
            <>
              {/* Location Card */}
              <div style={styles.locationInfo}>
                <p style={styles.locationName}>{locationData?.store_name}</p>
                <p style={styles.locationAddress}>
                  {locationData?.address && `${locationData.address}, `}
                  {locationData?.city}, {locationData?.state} {locationData?.zip_code}
                </p>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  Full Name <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Your full legal name"
                  value={form.fullName}
                  onChange={(e) => setForm(prev => ({ ...prev, fullName: e.target.value }))}
                  style={styles.input}
                />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  Your Role <span style={styles.required}>*</span>
                </label>
                <select
                  value={form.role}
                  onChange={(e) => setForm(prev => ({ ...prev, role: e.target.value }))}
                  style={styles.select}
                >
                  <option value="">Select your role</option>
                  <option value="Owner">Owner</option>
                  <option value="Manager">Manager</option>
                  <option value="Authorized Representative">Authorized Representative</option>
                </select>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  Business Email <span style={styles.required}>*</span>
                </label>
                <input
                  type="email"
                  placeholder="you@yourbusiness.com"
                  value={form.contactEmail}
                  onChange={(e) => setForm(prev => ({ ...prev, contactEmail: e.target.value }))}
                  style={styles.input}
                />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>Business Phone</label>
                <input
                  type="tel"
                  placeholder="(555) 555-5555"
                  value={form.contactPhone}
                  onChange={(e) => setForm(prev => ({ ...prev, contactPhone: e.target.value }))}
                  style={styles.input}
                />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>Additional Notes</label>
                <textarea
                  placeholder="Anything else you'd like us to know about your claim..."
                  value={form.notes}
                  onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                  style={styles.textarea}
                />
              </div>

              <button
                style={{
                  ...styles.button,
                  ...(!canProceedStep1 ? styles.buttonDisabled : {}),
                }}
                onClick={() => canProceedStep1 && setStep(2)}
                disabled={!canProceedStep1}
              >
                Next
              </button>
            </>
          )}

          {/* STEP 2: Agreement */}
          {step === 2 && (
            <>
              <div style={styles.agreementBox}>
                <p style={{ fontWeight: 600, marginTop: 0, color: theme.text }}>
                  CardChase Location Partner Agreement (v{AGREEMENT_VERSION})
                </p>
                <p>
                  By signing this agreement, you confirm that you are authorized to represent this business location on the CardChase platform.
                </p>
                <p><strong>You agree to:</strong></p>
                <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
                  <li>Provide accurate and current business information</li>
                  <li>Keep operating hours and event schedules up to date</li>
                  <li>Maintain a safe and welcoming environment for CardChase users</li>
                  <li>Not misrepresent your business or affiliation with CardChase</li>
                  <li>Comply with all applicable laws</li>
                </ul>
                <p><strong>You acknowledge that:</strong></p>
                <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
                  <li>CardChase is a listing platform, not an endorser or business partner</li>
                  <li>CardChase is not liable for any incidents at your location</li>
                  <li>Your verified status can be revoked for violation of these terms</li>
                  <li>Ownership disputes will be reviewed and decided by the CardChase team</li>
                </ul>
                <p>
                  CardChase may update this agreement. Continued participation constitutes acceptance of updated terms.
                </p>
              </div>

              <button
                style={styles.viewFullLink}
                onClick={() => navigate('/legal/location-partner-agreement')}
              >
                View Full Agreement
              </button>

              <div
                style={styles.checkboxRow}
                onClick={() => setAgreedToTerms(!agreedToTerms)}
              >
                <div style={{
                  ...styles.checkbox,
                  ...(agreedToTerms ? styles.checkboxChecked : {}),
                }}>
                  {agreedToTerms && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                <span style={styles.checkboxLabel}>
                  I have read and agree to the Location Partner Agreement
                </span>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  Type your full name as your digital signature <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Full legal name"
                  value={signatureName}
                  onChange={(e) => setSignatureName(e.target.value)}
                  style={{
                    ...styles.input,
                    fontStyle: 'italic',
                    fontSize: 17,
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  style={{
                    ...styles.button,
                    backgroundColor: 'transparent',
                    color: theme.textSecondary,
                    border: `1px solid ${theme.border}`,
                    flex: 1,
                  }}
                  onClick={() => setStep(1)}
                >
                  Back
                </button>
                <button
                  style={{
                    ...styles.button,
                    ...(!canSubmit ? styles.buttonDisabled : {}),
                    flex: 2,
                  }}
                  onClick={handleSubmit}
                  disabled={!canSubmit || submitting}
                >
                  <ShieldIcon />
                  {submitting ? 'Submitting...' : 'Submit Claim'}
                </button>
              </div>
            </>
          )}

          {/* STEP 3: Success */}
          {step === 3 && (
            <div style={styles.successContainer}>
              <div style={styles.successCircle}>
                <CheckIcon />
              </div>
              <h3 style={styles.successTitle}>Claim Submitted!</h3>
              <p style={styles.successText}>
                Our team will review your claim and verify your ownership. This process typically takes 1-3 business days.
              </p>
              <p style={styles.successText}>
                You'll receive an email confirmation at <strong>{form.contactEmail}</strong>.
              </p>
              <p style={{ ...styles.successText, color: theme.textMuted, fontSize: 13, marginTop: 16 }}>
                You can check your claim status from your profile.
              </p>
              <button
                style={{ ...styles.button, marginTop: 20 }}
                onClick={onClose}
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ClaimLocationModal;
